/** Seuils de dégâts par palier d’étoiles (œuf pension) — source unique client + serveur. */
export const DAYCARE_HATCH_DAMAGE: Readonly<Record<number, number>> = {
  1: 10_000,
  2: 30_000,
  3: 250_000,
  4: 500_000,
  5: 1_000_000,
}

export type DaycareSlotProgress = {
  slug: string
  isShiny: boolean
  stars: number
  damageDealt: number
  damageRequired: number
  /** Identifiant unique par dépôt — évite de fusionner la progression d’un œuf précédent (même espèce). */
  depositId?: string
  isShinyResult?: boolean
  loopSameSpecies?: boolean
  [key: string]: unknown
}

function daycareSlotKey(slot: Pick<DaycareSlotProgress, 'slug' | 'isShiny'>): string {
  return `${slot.slug}:${slot.isShiny ? 1 : 0}`
}

export function hasDaycareDepositId(slot: Pick<DaycareSlotProgress, 'depositId'>): boolean {
  const id = slot.depositId
  return typeof id === 'string' && id.trim().length > 0 && id.length <= 64
}

export function createDaycareDepositId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `dc-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
}

function slotMergeKey(slot: DaycareSlotProgress): string {
  if (hasDaycareDepositId(slot)) return `id:${String(slot.depositId).trim()}`
  return `legacy:${daycareSlotKey(slot)}`
}

/** Progression « fraîche » (nouvel œuf) vs entrée legacy complète (backup / ancien cycle). */
function shouldDropLegacySlotForTrackedDeposit(legacy: DaycareSlotProgress, tracked: DaycareSlotProgress): boolean {
  const reqL = Math.max(1, Number(legacy.damageRequired) || 1)
  const reqT = Math.max(1, Number(tracked.damageRequired) || 1)
  const legacyComplete = (Number(legacy.damageDealt) || 0) >= reqL
  const trackedFresh = (Number(tracked.damageDealt) || 0) < reqT * 0.25
  return legacyComplete && trackedFresh
}

function mergeTwoDaycareSlots<T extends DaycareSlotProgress>(prev: T, slot: T): T {
  const sRaw = Number(slot.stars)
  const slotStars =
    Number.isFinite(sRaw) && sRaw >= 1 ? Math.max(1, Math.min(5, Math.floor(sRaw))) : prev.stars
  const dRaw = Number(slot.damageDealt)
  const slotDealt = Number.isFinite(dRaw) && dRaw >= 0 ? Math.max(0, dRaw) : prev.damageDealt
  const depositId = hasDaycareDepositId(slot)
    ? String(slot.depositId).trim()
    : hasDaycareDepositId(prev)
      ? String(prev.depositId).trim()
      : undefined
  return reconcileDaycareSlotStarsAndDamage({
    ...prev,
    ...slot,
    stars: Math.max(prev.stars, slotStars),
    damageDealt: Math.max(prev.damageDealt, slotDealt),
    isShinyResult: prev.isShinyResult ?? slot.isShinyResult,
    loopSameSpecies: prev.loopSameSpecies !== false || slot.loopSameSpecies !== false,
    ...(depositId ? { depositId } : {}),
  }) as T
}

/** Aligne le seuil œuf sur les étoiles. Ne déduit jamais les ★ depuis damageRequired. */
export function reconcileDaycareSlotStarsAndDamage<T extends Pick<DaycareSlotProgress, 'stars' | 'damageRequired'>>(
  slot: T
): T {
  const stars = Math.max(1, Math.min(5, Math.floor(Number(slot.stars) || 1)))
  const damageRequired = DAYCARE_HATCH_DAMAGE[stars] ?? DAYCARE_HATCH_DAMAGE[5]!
  return { ...slot, stars, damageRequired } as T
}

/**
 * Fusionne pension serveur + client en gardant la progression la plus avancée par espèce.
 * Évite les rollbacks au /load et les écrasements par un onglet ou une save obsolète.
 */
export function mergeDaycareSlotsPreservingProgress<T extends DaycareSlotProgress>(
  serverSlots: T[],
  localSlots: T[],
  maxSlots: number
): T[] {
  const merged = new Map<string, T>()
  const apply = (raw: T) => {
    const slot = reconcileDaycareSlotStarsAndDamage(raw)
    const key = slotMergeKey(slot)
    const prev = merged.get(key)
    if (!prev) {
      merged.set(key, slot)
      return
    }
    merged.set(key, mergeTwoDaycareSlots(prev, slot))
  }
  for (const s of serverSlots) apply(s)
  for (const s of localSlots) apply(s)

  const values = [...merged.values()]
  const withDepositId = values.filter((s) => hasDaycareDepositId(s))
  const legacyOnly = values.filter((s) => !hasDaycareDepositId(s))
  const speciesWithTrackedDeposit = new Set(withDepositId.map((s) => daycareSlotKey(s)))
  const filteredLegacy = legacyOnly.filter((s) => !speciesWithTrackedDeposit.has(daycareSlotKey(s)))

  const bySpecies = new Map<string, T>()
  for (const slot of [...withDepositId, ...filteredLegacy]) {
    const sk = daycareSlotKey(slot)
    const prev = bySpecies.get(sk)
    if (!prev) {
      bySpecies.set(sk, slot)
      continue
    }
    const slotTracked = hasDaycareDepositId(slot)
    const prevTracked = hasDaycareDepositId(prev)
    if (slotTracked && !prevTracked) {
      if (shouldDropLegacySlotForTrackedDeposit(prev, slot)) {
        bySpecies.set(sk, slot)
      } else {
        bySpecies.set(sk, mergeTwoDaycareSlots(slot, prev))
      }
    } else if (!slotTracked && prevTracked) {
      if (shouldDropLegacySlotForTrackedDeposit(slot, prev)) {
        continue
      }
      bySpecies.set(sk, mergeTwoDaycareSlots(prev, slot))
    } else {
      bySpecies.set(sk, mergeTwoDaycareSlots(prev, slot))
    }
  }
  return [...bySpecies.values()].slice(0, maxSlots)
}

/** Palier d’œuf suivant après une éclosion (plafond 5★). */
export function daycareSlotStarsAfterHatch(currentStars: number): number {
  const s = Math.max(1, Math.min(5, Math.floor(Number(currentStars) || 1)))
  return Math.min(5, s + 1)
}

export type DaycareQueueEntryLike = {
  slug: string
  isShiny: boolean
  stars: number
  nameFr?: string
  nameEn?: string
  rarity?: string
  cosmeticFormSlug?: string | null
}

function queueEntryKey(entry: Pick<DaycareQueueEntryLike, 'slug' | 'isShiny'>): string {
  return `${entry.slug}:${entry.isShiny ? 1 : 0}`
}

/**
 * Fusionne files pension (reserve / lastDeposited) : ordre client prioritaire,
 * étoiles = max par espèce, entrées serveur-only conservées en fin de liste.
 */
export function mergeDaycareQueueEntries<T extends DaycareQueueEntryLike>(
  serverEntries: T[],
  clientEntries: T[],
  maxEntries: number
): T[] {
  const byKey = new Map<string, T>()
  const normalize = (raw: T): string | null => {
    const slug = typeof raw.slug === 'string' ? raw.slug.trim() : ''
    if (!slug) return null
    const stars = Math.max(1, Math.min(5, Math.floor(Number(raw.stars) || 1)))
    const key = queueEntryKey({ slug, isShiny: Boolean(raw.isShiny) })
    const prev = byKey.get(key)
    byKey.set(
      key,
      prev ? ({ ...prev, ...raw, stars: Math.max(prev.stars, stars) } as T) : ({ ...raw, stars } as T)
    )
    return key
  }
  for (const e of serverEntries) normalize(e)
  const clientOrder: string[] = []
  for (const e of clientEntries) {
    const key = normalize(e)
    if (key && !clientOrder.includes(key)) clientOrder.push(key)
  }
  const serverOnly: string[] = []
  for (const e of serverEntries) {
    const slug = typeof e.slug === 'string' ? e.slug.trim() : ''
    if (!slug) continue
    const key = queueEntryKey({ slug, isShiny: Boolean(e.isShiny) })
    if (!clientOrder.includes(key) && !serverOnly.includes(key)) serverOnly.push(key)
  }
  return [...clientOrder, ...serverOnly]
    .map((key) => byKey.get(key))
    .filter((e): e is T => Boolean(e))
    .slice(0, maxEntries)
}

export function mergeDaycareReserveFillPending(serverValue: number, clientValue: number): number {
  return Math.max(
    0,
    Math.floor(Number(serverValue) || 0),
    Math.floor(Number(clientValue) || 0)
  )
}

/** Sauvegarde probablement incomplète (ex. entre deux éclosions) : ne pas écraser la file serveur. */
export function isLikelyStaleDaycareQueueSave<T extends DaycareQueueEntryLike>(
  clientEntries: readonly T[],
  serverEntries: readonly T[]
): boolean {
  if (clientEntries.length === 0 || serverEntries.length === 0) return false
  if (clientEntries.length >= serverEntries.length) return false
  const serverKeys = new Set(serverEntries.map((e) => queueEntryKey(e)))
  return clientEntries.every((e) => serverKeys.has(queueEntryKey(e)))
}

/**
 * Fusion files à la sauvegarde : garde la progression (★ max) et évite qu’une payload
 * partielle (moins d’entrées que le serveur) n’impose un ordre obsolète.
 */
export function mergeDaycareQueueEntriesOnSave<T extends DaycareQueueEntryLike>(
  serverEntries: T[],
  clientEntries: T[],
  maxEntries: number
): T[] {
  if (clientEntries.length === 0) {
    return serverEntries.slice(0, maxEntries)
  }
  if (isLikelyStaleDaycareQueueSave(clientEntries, serverEntries)) {
    return mergeDaycareQueueEntries(clientEntries, serverEntries, maxEntries)
  }
  return mergeDaycareQueueEntries(serverEntries, clientEntries, maxEntries)
}

/** Champs pension absents ou vides côté client : conserver le serveur (sauf clear explicite). */
export function mergeDaycareSlotsOnSave<T extends DaycareSlotProgress>(
  serverSlots: T[],
  clientSlots: T[],
  maxSlots: number
): T[] {
  if (clientSlots.length === 0) {
    return serverSlots.slice(0, maxSlots)
  }
  return mergeDaycareSlotsPreservingProgress(serverSlots, clientSlots, maxSlots)
}

export function mergeDaycareQueueOnSave<T extends DaycareQueueEntryLike>(
  serverEntries: T[],
  clientEntries: T[],
  maxEntries: number
): T[] {
  if (clientEntries.length === 0) {
    return serverEntries.slice(0, maxEntries)
  }
  return mergeDaycareQueueEntriesOnSave(serverEntries, clientEntries, maxEntries)
}

/**
 * Instantané des espèces présentes (slots + file de réserve) au moment d’envoyer une
 * requête pension. Sert à détecter les dépôts faits pendant que la requête était en vol,
 * pour les re-poser après l’écrasement du store par la réponse serveur
 * (bug « dépôt dégagé à la fin de la sync »).
 */
export type DaycareMidFlightSnapshot = {
  slotKeys: ReadonlySet<string>
  reserveKeys: ReadonlySet<string>
}

/** Clé espèce (slug + shiny) — identité pension : slots et files sont uniques par espèce. */
export function daycareSpeciesKey(entry: Pick<DaycareQueueEntryLike, 'slug' | 'isShiny'>): string {
  return queueEntryKey(entry)
}

export function captureDaycareMidFlightSnapshot(
  slots: readonly Pick<DaycareSlotProgress, 'slug' | 'isShiny'>[],
  reserveQueue: readonly Pick<DaycareQueueEntryLike, 'slug' | 'isShiny'>[]
): DaycareMidFlightSnapshot {
  return {
    slotKeys: new Set(slots.map((s) => daycareSpeciesKey(s))),
    reserveKeys: new Set(reserveQueue.map((e) => daycareSpeciesKey(e))),
  }
}

/** Slots / entrées de réserve apparus après la prise du snapshot (dépôts pendant la requête). */
export function diffDaycareMidFlightAdditions<
  S extends Pick<DaycareSlotProgress, 'slug' | 'isShiny'>,
  Q extends Pick<DaycareQueueEntryLike, 'slug' | 'isShiny'>,
>(
  snapshot: DaycareMidFlightSnapshot,
  currentSlots: readonly S[],
  currentReserve: readonly Q[]
): { addedSlots: S[]; addedReserve: Q[] } {
  return {
    addedSlots: currentSlots.filter((s) => !snapshot.slotKeys.has(daycareSpeciesKey(s))),
    addedReserve: currentReserve.filter((e) => !snapshot.reserveKeys.has(daycareSpeciesKey(e))),
  }
}

/**
 * Espèces présentes au snapshot (slots ou réserve) mais plus nulle part dans l’état local :
 * retraits faits pendant la requête. Un déplacement slot ↔ réserve n’est PAS un retrait
 * (l’espèce reste présente), seuls les départs complets de la pension sont retournés.
 */
export function diffDaycareMidFlightRemovals(
  snapshot: DaycareMidFlightSnapshot,
  currentSlots: readonly Pick<DaycareSlotProgress, 'slug' | 'isShiny'>[],
  currentReserve: readonly Pick<DaycareQueueEntryLike, 'slug' | 'isShiny'>[]
): Set<string> {
  const localKeys = new Set<string>([
    ...currentSlots.map((s) => daycareSpeciesKey(s)),
    ...currentReserve.map((e) => daycareSpeciesKey(e)),
  ])
  const removed = new Set<string>()
  for (const key of [...snapshot.slotKeys, ...snapshot.reserveKeys]) {
    if (!localKeys.has(key)) removed.add(key)
  }
  return removed
}

/** Pension entièrement vidée côté client (slots + files) — doit écraser l’état serveur, pas fusionner. */
export function isDaycareExplicitlyCleared(payload: {
  slots: readonly unknown[]
  lastDeposited: readonly unknown[]
  reserveQueue: readonly unknown[]
}): boolean {
  return (
    payload.slots.length === 0 &&
    payload.lastDeposited.length === 0 &&
    payload.reserveQueue.length === 0
  )
}
